/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconEye } from "../../../shared/assets/Icons/Icon_Eye.svg";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FilterField,
  labelId,
  ParsedFilter as OldParsed,
  parseToken,
} from "kontrolEdge/helpers/advancedFilters";
import { useNavigate } from "react-router-dom";
import {
  Quotation,
  fwaasColumns as baseColumns,
  fwaasColumns,
  shapeFwaasData,
} from "./QuotationData";
import { useAppSelector } from "@redux/hooks";
import { makeQuotationFields } from "./QuotationFieldSpec";
import { fetchFwaasList } from "./request/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAuth0 } from "@auth0/auth0-react";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import {
  buildConnItemPredicate,
  canonicalStatus,
  connectionKeyMatches,
  ConnectionType,
  parseConnToken,
  statusLabel,
} from "./QuotationsHelpers";
import { buildDetailPath, serviceDetailPath } from "@shared/navigation/tabs/detailRoutes";
import "@n8n/chat/style.css";
import { createChat } from "@n8n/chat";

const NO_QUOTATIONS: Quotation[] = [];

export default function QuotationsPage() {
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;
  const api = useAxiosAuth0();
  const { getAccessTokenSilently } = useAuth0();

  // Cached per tenant: coming back paints the last answer at once and refreshes it
  // underneath. Same endpoint as the FWaaS list but shaped as quotations, so it keeps
  // its own key — sharing FwaasPage's would paint one page's rows in the other's shape.
  // A failure leaves the table empty, as before (this page has no error state).
  const {
    data: quotationList = NO_QUOTATIONS,
    isLoading: isLoadingList,
    isRefreshing: isRefreshingList,
  } = useCachedFetch(
    tenantId
      ? `quotations/kontroledge/fwaas?tenantId=${tenantId}&includeChildServices=true&includeFWInt=true`
      : null,
    async () => {
      const { status, data } = await fetchFwaasList(api, tenantId);
      if (status >= 500 || data.success === false) {
        throw new Error(data.message || "Unknown server error");
      }
      return Array.isArray(data.data) ? shapeFwaasData(data.data) : [];
    }
  );
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const navigate = useNavigate();

  const chatInitRef = useRef(false);

  useEffect(() => {
    if (chatInitRef.current) return;
    chatInitRef.current = true;

    let cancelled = false;
    (async () => {
      // Token para headers hacia n8n (opcional)
      let token: string | undefined;
      try {
        token = await getAccessTokenSilently();
      } catch {
        // si no hay token, seguimos sin auth
      }
      if (cancelled) return;
      // Asegura que el contenedor exista
      const targetId = "n8n-chat-target";
      if (!document.getElementById(targetId)) {
        const el = document.createElement("div");
        el.id = targetId;
        document.body.appendChild(el);
      }
      // Inicializa el chat dentro del panel derecho
      createChat({
        webhookUrl: process.env.VITE_N8N_CHAT_WEBHOOK_URL || "",
        target: "#n8n-chat-target",
        mode: "fullscreen",
        loadPreviousSession: true,
        enableStreaming: true,
        chatSessionKey: `n8n_session_${tenantId || "anonymous"}`,
        metadata: {
          tenantId,
          partnerId,
          source: "quotations",
        },
        webhookConfig: {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "x-tenant-id": String(tenantId ?? ""),
          },
        },
        // i18n / initialMessages / allowFileUploads (opcionales)
        initialMessages: ["What can we help you with?"],
        allowFileUploads: true,
        allowedFilesMimeTypes: "image/*,application/pdf",
        i18n: {
          en: {
            title: '👋 Hi! there',
            subtitle: "Start a chat. We're here to help you.",
            footer: '',
            getStarted: 'New Conversation',
            inputPlaceholder: 'Type your question..',
            closeButtonTooltip: ""
          },
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, tenantId, partnerId]);

  const quotationFields = useMemo(
    () => makeQuotationFields(isViewingAllCustomers),
    [isViewingAllCustomers]
  );

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of quotationFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [quotationFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Quotation>>();
    for (const f of quotationFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [quotationFields]);

  const suggestedKeys = useMemo(() => {
    const headers = fwaasColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }

    // Ensure our custom keys show up in the searchbar suggestions
    const extra = ["Service ID", "Status", "Connection"];
    for (const k of extra) {
      if (!headers.includes(k)) headers.push(k);
    }
    return headers;
  }, [fwaasColumns, isViewingAllCustomers]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  type StringOp = ":" | "=";

  const predicate = useMemo(() => {
    const filters = normalizedFilters;
    const norm = (x: unknown) => String(x ?? "").toLowerCase();
    const matchOp = (hay: string, needle: string, op: StringOp = ":") =>
      !needle ? true : op === "=" ? hay === needle : hay.includes(needle);
    const toStringOp = (o?: ":" | "=" | ">" | "<" | ">=" | "<="): StringOp =>
      o === "=" ? "=" : ":";

    const serviceKeyMatches = (k: string) => {
      const kk = k.replace(/\s+/g, "").toLowerCase();
      return kk === "serviceid" || kk === "service-id";
    };

    const connTokens = filters
    .filter((f) => f.key && connectionKeyMatches(f.key))
    .map((f) => parseConnToken(String(f.value ?? ""), toStringOp(f.op)));

    return (row: Quotation) => {
      if (!filters.length) return true;

      const rowServiceId = norm(row.serviceId);
      const rowStatusCanon = canonicalStatus(row.status);
      const rowStatusLabel = statusLabel(row.status).toLowerCase();

      return filters.every((f) => {
        const key = (f.key || "").trim().toLowerCase();
        const valueRaw = String(f.value ?? "");
        const value = valueRaw.toLowerCase();
        const sop = toStringOp(f.op);

        if (!key) {
          return (
            matchOp(rowServiceId, value, ":") ||
            matchOp(rowStatusLabel, value, ":") ||
            matchOp(rowStatusCanon, value, ":") 
          );
        }

        if (serviceKeyMatches(key)) {
          return matchOp(rowServiceId, value, sop);
        }

        if (key === "status") {
          if (sop === "=") {
            // Exact: compare canonical values (so Status="Active" matches)
            return canonicalStatus(valueRaw) === rowStatusCanon;
          }
          // Contains: allow label or canonical partials, e.g. "act", "down"
          return matchOp(rowStatusLabel, value, ":") || matchOp(rowStatusCanon, value, ":");
        }

        if (key === "customer") {
          const cust = norm(row.customer?.name);
          return matchOp(cust, value, sop);
        }

        return false;
      });
    };
  }, [normalizedFilters]);

  const connItemFilterFn = useMemo(() => {
    const filters = normalizedFilters;
    const connTokens = filters
      .filter((f) => f.key && connectionKeyMatches(f.key))
      .map((f) => parseConnToken(String(f.value ?? ""), f.op === "=" ? "=" : ":"));
    return buildConnItemPredicate(connTokens);
  }, [normalizedFilters]);


  const handleConnectionClick = useCallback(
    (type: ConnectionType, idFromCell: string, row: Quotation) => {
      // idFromCell is the nested connections[*].id (string). Fallback just in case:
      const id = idFromCell ?? "";
      navigate(serviceDetailPath(type, id), { state: { from: "fwaas", row } });
    },
    [navigate]
  );

  const filteredData = useMemo(
    () => quotationList.filter(predicate),
    [quotationList, predicate]
  );

  const redirectTo = useMemo(
    () => new URL("/kontrol", window.location.origin).toString(),
    []
  );

  const columns = useMemo<GenericTableColumn<Quotation>[]>(() => {
    const cols = [...baseColumns];
    if (isViewingAllCustomers) {
      cols.splice(0, 0, {
        header: "Customer",
        accessor: "customer",
        sortAccessor: (row: Quotation) => row.customer?.name ?? "",
        renderCell: (row: Quotation) =>
          row.customer ? (
            <div className="flex flex-col">
              <span className="font-medium">{row.customer.name}</span>
              <span className="text-xs text-gray-500">
                ID: {row.customer.chargebeeCustomerId}
              </span>
            </div>
          ) : (
            "—"
          ),
      });
    }

    return cols.map((col) => {
      if (col.header === "Quotation ID") {
        return {
          ...col,
          renderCell: (row: Quotation) => (
            <button
              {...viewHref(buildDetailPath("port", { portId: row.id }))}
              onClick={() => {
                navigate(buildDetailPath("port", { portId: row.id }), {
                  state: { selectedPortRow: row, portIdString: row.serviceId },
                });
              }}
              className="
              hover:text-semantic-text-brand 
              hover:underline
              focus:underline 
              active:underline
              font-medium
              underline
              text-semantic-text-brand
              "
            >
              {row.serviceId}
            </button>
          ),
        };
      }
      if (col.header === "Details") {
        return {
          ...col,
          renderCell: (row: Quotation) => (
            <button
              className="
					flex items-center justify-center
					border border-semantic-border-secondary
					rounded
					text-semantic-text-secondary
					hover:bg-semantic-state-active
					w-10 h-10
				"
              onClick={()=> {console.log('clicking')}}
            >
              <IconEye className="w-6 h-6" />
            </button>
          ),
        };
      }
      return col;
    });
  }, [baseColumns, isViewingAllCustomers, connItemFilterFn, handleConnectionClick, navigate]);

  return (
    <div className="flex w-full gap-4">
      {/* Izquierda: 3/4 tabla */}
      <div className="relative w-3/4">
        <RefreshingIndicator active={isRefreshingList} />
        {isLoadingList ? (
          // Only the table waits: the chat panel on the right stays mounted, since
          // createChat renders into #n8n-chat-target.
          <ListPageSkeleton columns={5} />
        ) : (
          <GenericCard isCollapsible={false}>
            <h3 className="px-5 pt-5">History</h3>
            <div className="flex gap-5 justify-between">
              <div className="w-[520px] m-5">
                <GenericSearchbar
                  placeholder="Find by attribute or value"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                />
              </div>
            </div>
            <div className="px-5 mb-5">
              <GenericTable
                columns={columns}
                data={filteredData}
                initialRowsPerPage={10}
                rowsPerPageOptions={[10, 20, 30]}
                width="100%"
                tableClassName="bg-semantic-bg-primary"
              />
            </div>
          </GenericCard>
        )}
      </div>

      {/* Derecha: 1/4 chat n8n */}
      <aside className="w-1/4">
        <div
          id="n8n-chat-target"
          className="h-[80vh] min-h-[560px] border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
        />
      </aside>
    </div>
  );
}
